import type { PlausibilityMode } from "@sitescene/types";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import { queryKeys } from "@/api/query-keys";
import { useOrgSelection } from "@/context/use-org-selection";

export interface ShadowTenantSettings {
	alertSeverityThreshold: number;
	claimsCheckEnabled: boolean;
	defaultFilterMode: string;
	plausibilityMode: PlausibilityMode;
	sensorHealthCheckEnabled: boolean;
}

/** Staff-only tenant settings of the selected organization. */
export const usePlausibilitySettings = () => {
	const { selected } = useOrgSelection();
	const orgCode = selected?.orgCode ?? null;
	return useQuery({
		enabled: orgCode !== null,
		queryFn: async () => {
			const response = await apiClient.get<ShadowTenantSettings>(
				ENDPOINTS.shadow.tenantSettings,
			);
			return response.data;
		},
		queryKey: queryKeys.plausibility.settings(orgCode ?? ""),
	});
};

/** The staff-only settings the page edits; a save sends only what changed. */
export type ShadowTenantSettingsUpdate = Partial<
	Pick<ShadowTenantSettings, "claimsCheckEnabled" | "plausibilityMode">
>;

/** Saves the verification mode, the footage check, or both, in one request. */
export const useUpdatePlausibilitySettings = () => {
	const queryClient = useQueryClient();
	const { selected } = useOrgSelection();
	const orgCode = selected?.orgCode ?? "";
	return useMutation({
		mutationFn: async (changes: ShadowTenantSettingsUpdate) => {
			const response = await apiClient.patch<ShadowTenantSettingsUpdate>(
				ENDPOINTS.shadow.tenantSettings,
				changes,
			);
			return response.data;
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: queryKeys.plausibility.settings(orgCode),
			});
		},
	});
};
